/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps, Fragment, Ref } from 'preact';
import { forwardRef } from 'preact/compat';
import { useCallback } from 'preact/hooks';
import { FocusableHandle, useFocusableTextField } from '../hooks/UNSAFE_useFocusableTextField';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { FormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { useHover } from '../hooks/UNSAFE_useHover';
import { useTextField } from '../hooks/UNSAFE_useTextField';
import { Label } from '../UNSAFE_Label';
import { usePrefixSuffix } from '../hooks/UNSAFE_usePrefixSuffix';

import {
  ReadonlyTextField,
  ReadonlyTextFieldInput,
  TextField,
  TextFieldInput
} from '../UNSAFE_TextField';
import { InlineUserAssistance } from '../UNSAFE_UserAssistance';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { Size } from '../utils/UNSAFE_size';
import { StepEvent, useSteppers } from './useSteppers';
import { ValueUpdateDetail } from '../utils/UNSAFE_valueUpdateDetail';
import { useCurrentValueReducer } from '../hooks/UNSAFE_useCurrentValueReducer';

type TextFieldInputProps = ComponentProps<typeof TextFieldInput>;

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type NumberInputTextProps = {
  /**
   * Controls whether the step buttons are shown.
   */
  hasSteppers?: boolean;

  /**
   * Controls whether the step down button is disabled.
   */
  isStepDownDisabled?: boolean;

  /**
   * Controls whether the step up button is disabled.
   */
  isStepUpDisabled?: boolean;

  /**
   * Called when a continuous step is requested (aka "spin")
   * by holding down the arrow up or arrow down keys, or
   * by pressing and holding down a step button.
   */
  onSpin?: (event: StepEvent) => void;

  /**
   * Called when spinning is complete and the user has
   * released the arrow key or step button.
   */
  onSpinComplete?: () => void;

  /**
   * Called when a step is requested by clicking a step button
   * or by pressing the arrow up or arrow down keys.
   */
  onStep?: (event: StepEvent) => void;
};

type Props = NumberInputTextProps & {
  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the input.
   */
  ariaDescribedBy?: TextFieldInputProps['ariaDescribedBy'];

  /**
   * The maximum allowed value. Used for the aria-valuemax attribute.
   */
  ariaValueMax?: TextFieldInputProps['ariaValueMax'];

  /**
   * The minimum allowed value. Used for the aria-valuemin attribute.
   */
  ariaValueMin?: TextFieldInputProps['ariaValueMin'];

  /**
   * The number value of the component. Used for the aria-valuenow attribute.
   */
  ariaValueNow?: TextFieldInputProps['ariaValueNow'];

  /**
   * The formatted value of the component. Used for the aria-valueText
   * attribute to provide a more human readable text alternative for
   * ariaValueNow.
   */
  ariaValueText?: TextFieldInputProps['ariaValueText'];

  /**
   * Text to provide guidance to help the user understand what data to enter.
   */
  assistiveText?: InlineUserAssistanceProps['assistiveText'];

  /**
   * Dictates component's autocomplete state.
   * This attribute indicates whether the value of the control can be automatically completed by the browser.
   * The common values are "on" and "off".
   */
  autoComplete?: TextFieldInputProps['autoComplete'];

  /**
   * Autofocus specifies whether the component will get focus when the page is loaded.
   * If it is set to true then the associated component will get input focus when the page is loaded.
   * Setting this property doesn't set the focus to the component: it tells the browser to focus
   * to it when the element is inserted in the document.
   */
  autoFocus?: TextFieldInputProps['autoFocus'];

  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];

  /**
   * Custom text to be rendered for the <code>helpSourceLink</code>.
   */
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];

  /**
   * Specifies whether the component is disabled.
   */
  isDisabled?: boolean;

  /**
   * Specifies whether the component is readonly.
   */
  isReadonly?: boolean;

  /**
   * Specifies for accessibility purposes whether a value is required.
   *
   * Setting this property to <code>false</code> means that a value is not required to be
   * committed by the user. Setting this property to <code>true</code> means that a value
   * is required to be committed by the user.
   */
  isRequired?: TextFieldInputProps['isRequired'];

  /**
   * Specifies whether to show an indicator on screen that a value is required, for example
   * before the user has committed a value.
   */
  isRequiredShown?: InlineUserAssistanceProps['isRequiredShown'];

  /**
   * Specifies the label associated with the field.
   */
  label: string;

  /**
   * Specifies where the label is positioned relative to the field.
   */
  labelEdge?: 'inside' | 'start' | 'top' | 'none';

  /**
   * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
   */
  labelStartWidth?: Size;

  /**
   * Messages to show on screen that are associated with the component.
   */
  messages?: InlineUserAssistanceProps['messages'];

  /**
   * A short hint to display before the user enters a value.
   */
  placeholder?: TextFieldInputProps['placeholder'];

  /**
   * The prefix displays as regular text before the input text, and inline with it.
   * The prefix is not editable or focusable.
   */
  prefix?: string;

  /**
   * The suffix displays as regular text after the input text, and inline with it.
   * The suffix is not editable or focusable.
   */
  suffix?: string;

  /**
   * Specifies how to align text within the field.
   */
  textAlign?: TextFieldInputProps['textAlign'];

  /**
   * Specifies the density of the user assistance presentation. It can be set to:
   * <ul>
   * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * <li><code>'reflow'</code>: Show inline. Layout will reflow when text is displayed.</li>
   * </ul>
   */
  userAssistanceDensity?: InlineUserAssistanceProps['userAssistanceDensity'];

  /**
   * The current possibly formatted display value of the component.
   */
  value?: TextFieldInputProps['value'];

  /**
   * The style variant of the component.
   */
  variant?: TextFieldInputProps['variant'];

  /**
   * The type of virtual keyboard to display for entering a value on mobile browsers.
   * This property has no effect on desktop browsers.
   */
  virtualKeyboard?: 'auto' | 'number' | 'text';

  /**
   * Callback invoked when the user commits the entered value by either blurring or pressing Enter.
   */
  onCommit?: TextFieldInputProps['onCommit'];

  /**
   * Callback invoked each time the user changes the value of the field. For example, if the user
   * types 'abcd', this callback will be called four times with the value as 'a', 'ab', 'abc' and 'abcd'
   * respectively.
   */
  onInput: TextFieldInputProps['onInput'];
};

export const NumberInputText = forwardRef(
  (
    {
      ariaDescribedBy,
      ariaValueMax,
      ariaValueMin,
      ariaValueNow,
      ariaValueText,
      assistiveText,
      autoComplete = 'off',
      autoFocus = false,
      helpSourceLink,
      helpSourceText,
      isDisabled: propIsDisabled,
      isReadonly: propIsReadonly,
      isRequired = false,
      isRequiredShown,
      isStepDownDisabled: propIsStepDownDisabled,
      isStepUpDisabled: propIsStepUpDisabled,
      hasSteppers = false,
      label,
      labelEdge: propLabelEdge,
      labelStartWidth: propLabelStartWidth,
      messages,
      placeholder,
      prefix,
      suffix,
      textAlign: propTextAlign,
      userAssistanceDensity: propUserAssistanceDensity,
      value,
      variant = 'default',
      virtualKeyboard,
      onInput,
      onCommit,
      onSpin,
      onSpinComplete,
      onStep
    }: Props,
    ref?: Ref<FocusableHandle>
  ) => {
    const { currentCommitValue, dispatch } = useCurrentValueReducer({ value });
    const onInputAndDispatch = useCallback(
      (detail: ValueUpdateDetail<string>) => {
        dispatch({ type: 'input', payload: detail.value });
        onInput?.(detail);
      },
      [onInput, dispatch]
    );
    const onCommitAndDispatch = useCallback(
      (detail: ValueUpdateDetail<string>) => {
        dispatch({ type: 'commit', payload: detail.value });
        onCommit?.(detail);
      },
      [onCommit, dispatch]
    );
    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      labelEdge: formLabelEdge,
      labelStartWidth: formLabelStartWidth,
      textAlign: formTextAlign,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();
    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const labelEdge = propLabelEdge ?? formLabelEdge;
    const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
    const textAlign = propTextAlign ?? formTextAlign;
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;

    const { enabledElementRef, focusProps, isFocused, readonlyElementRef } = useFocusableTextField<
      HTMLInputElement,
      HTMLDivElement
    >({ isDisabled, isReadonly, ref });
    const { hoverProps } = useHover({ isDisabled: isReadonly || isDisabled || false });
    const {
      baseId,
      formFieldContext,
      inputProps,
      labelProps,
      textFieldProps,
      userAssistanceProps
    } = useTextField({
      ariaDescribedBy,
      isDisabled,
      isFocused,
      isReadonly,
      labelEdge,
      messages,
      styleVariant: variant,
      value
    });

    // Disable the steppers if the number input is disabled.
    const isStepDownDisabled = isDisabled || propIsStepDownDisabled;
    const isStepUpDisabled = isDisabled || propIsStepUpDisabled;
    // stepHandlerProps contains keyup and keydown handlers
    // for stepping using the arrow up and down keys.
    const { steppers, stepHandlerProps } = useSteppers({
      hasSteppers,
      inputRef: enabledElementRef,
      isStepDownDisabled,
      isStepUpDisabled,
      onSpin,
      onSpinComplete,
      onStep
    });

    const {
      renderedPrefix,
      renderedSuffix,
      text: valuePrefixSuffix,
      ariaLabelledBy
    } = usePrefixSuffix({
      baseId,
      hasEndContent: steppers !== undefined,
      hasInsideLabel: label !== undefined && labelEdge === 'inside',
      hasStartContent: false,
      hasValue: formFieldContext.hasValue,
      isDisabled,
      isFocused,
      labelId: labelProps.id,
      prefix,
      suffix,
      value
    });

    const labelComp = labelEdge !== 'none' ? <Label {...labelProps}>{label}</Label> : undefined;

    const fieldLabelProps = {
      label: labelEdge !== 'none' ? labelComp : undefined,
      labelEdge: labelEdge !== 'none' ? labelEdge : undefined,
      labelStartWidth: labelEdge !== 'none' ? labelStartWidth : undefined
    };

    const ariaLabel = labelEdge === 'none' ? label : undefined;

    const inlineUserAssistance =
      isDisabled || isReadonly ? (
        // save space for user assistance if density is 'efficient', even though we don't
        // render user assistance for disabled or readonly fields
        userAssistanceDensity !== 'efficient' ? undefined : (
          <InlineUserAssistance
            userAssistanceDensity={userAssistanceDensity}
            {...userAssistanceProps}
          />
        )
      ) : (
        <InlineUserAssistance
          assistiveText={assistiveText}
          fieldLabel={label}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          messages={messages}
          isRequiredShown={isRequiredShown}
          userAssistanceDensity={userAssistanceDensity}
          {...userAssistanceProps}
        />
      );

    if (isReadonly) {
      return (
        <FormFieldContext.Provider value={formFieldContext}>
          <ReadonlyTextField
            role="presentation"
            inlineUserAssistance={inlineUserAssistance}
            onBlur={focusProps.onfocusout}
            onFocus={focusProps.onfocusin}
            {...fieldLabelProps}>
            <ReadonlyTextFieldInput
              ariaDescribedBy={ariaDescribedBy}
              ariaLabel={ariaLabel}
              ariaLabelledBy={labelProps.id}
              as="div"
              autoFocus={autoFocus}
              elementRef={readonlyElementRef}
              textAlign={textAlign}
              value={valuePrefixSuffix}
              hasInsideLabel={label !== undefined && labelEdge === 'inside'}
            />
          </ReadonlyTextField>
        </FormFieldContext.Provider>
      );
    }

    // If ariaValueText is the same as ariaValueNow.toString, no need to show it.
    const valueText =
      ariaValueNow === undefined
        ? ariaValueText
        : ariaValueNow.toString() !== ariaValueText
        ? ariaValueText
        : undefined;

    const mainContent = (
      <Fragment>
        {renderedPrefix}
        <TextFieldInput
          ariaLabelledBy={ariaLabelledBy}
          ariaLabel={ariaLabel}
          ariaValueMax={ariaValueMax}
          ariaValueMin={ariaValueMin}
          ariaValueNow={ariaValueNow}
          ariaValueText={valueText}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          currentCommitValue={currentCommitValue}
          hasEndContent={steppers !== undefined}
          hasInsideLabel={labelComp !== undefined && labelEdge === 'inside'}
          hasPrefix={renderedPrefix !== undefined}
          hasStartContent={false}
          hasSuffix={renderedSuffix !== undefined}
          inputRef={enabledElementRef}
          isRequired={isRequired}
          placeholder={placeholder}
          role={hasSteppers ? 'spinbutton' : undefined}
          textAlign={textAlign}
          type={isMobile() ? virtualKeyboard : undefined}
          value={isDisabled ? valuePrefixSuffix : value}
          onCommit={onCommitAndDispatch}
          onInput={onInputAndDispatch}
          {...inputProps}
          {...stepHandlerProps}
        />
        {renderedSuffix}
      </Fragment>
    );

    return (
      <FormFieldContext.Provider value={formFieldContext}>
        <TextField
          endContent={steppers}
          inlineUserAssistance={inlineUserAssistance}
          mainContent={mainContent}
          onBlur={focusProps.onfocusout}
          onFocus={focusProps.onfocusin}
          {...textFieldProps}
          {...fieldLabelProps}
          {...hoverProps}
        />
      </FormFieldContext.Provider>
    );
  }
);

/**
 * Helper function to determine whether the current device is a mobile device
 * @returns true if runnning on a mobile device, false otherwise
 */
function isMobile() {
  const deviceType = getClientHints().deviceType;
  return deviceType === 'phone' || deviceType === 'tablet';
}
